import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "../lib/router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { MainLayout } from "../components/layout";
import { AttendanceTodaySummaryCard } from "../components/features/attendance/AttendanceTodaySummaryCard";

export function MainLeave() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    window.location.href = "/login";
    return null;
  }

  return (
    <MainLayout>
      {/* Welcome Banner */}
      <div className="mb-8">
        <Card className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 border-0 shadow-xl">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="text-white">
                <h2 className="text-3xl font-bold mb-2">
                  👋 ยินดีต้อนรับ, {user.firstName}!
                </h2>
                <p className="text-blue-100 dark:text-blue-200 text-lg mb-3">
                  รหัสพนักงาน:{" "}
                  <span className="font-semibold">{user.employeeId}</span>
                  {user.position && (
                    <>
                      {" "}
                      | ตำแหน่ง:{" "}
                      <span className="font-semibold">{user.position}</span>
                    </>
                  )}
                </p>
                <p className="text-blue-50 dark:text-blue-100 max-w-2xl">
                  คุณสามารถจัดการคำขอลางาน ตรวจสอบสิทธิ์การลา
                  และดูประวัติการลาได้ที่นี่
                </p>
              </div>
              <Button
                onClick={() => navigate("/leave/request")}
                className="bg-white text-blue-600 hover:bg-blue-50 dark:bg-gray-100 dark:text-blue-700 dark:hover:bg-gray-200 shadow-lg hover:shadow-xl transition-all text-lg px-8 py-6 font-semibold"
                size="lg"
              >
                📝 ขอลางาน
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mb-6">
        <AttendanceTodaySummaryCard />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Leave Balance Card */}
        <Card className="border-blue-100 dark:border-blue-900 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950 border-b dark:border-gray-700">
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <span className="text-xl">🏖️</span>
              สิทธิ์การลา
            </CardTitle>
            <CardDescription className="dark:text-gray-400">
              วันลาคงเหลือของคุณ
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-100 dark:border-blue-900">
                <div className="flex items-center gap-2">
                  <span className="text-xl">🏖️</span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    ลาพักร้อน
                  </span>
                </div>
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  10{" "}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    วัน
                  </span>
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-50 dark:bg-red-950/50 rounded-lg border border-red-100 dark:border-red-900">
                <div className="flex items-center gap-2">
                  <span className="text-xl">🤒</span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    ลาป่วย
                  </span>
                </div>
                <span className="text-2xl font-bold text-red-600 dark:text-red-400">
                  30{" "}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    วัน
                  </span>
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-amber-50 dark:bg-amber-950/50 rounded-lg border border-amber-100 dark:border-amber-900">
                <div className="flex items-center gap-2">
                  <span className="text-xl">📋</span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    ลากิจ
                  </span>
                </div>
                <span className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                  3{" "}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    วัน
                  </span>
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Requests Card */}
        <Card className="border-green-100 dark:border-green-900 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border-b dark:border-gray-700">
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <span className="text-xl">📝</span>
              คำขอล่าสุด
            </CardTitle>
            <CardDescription className="dark:text-gray-400">
              คำขอลาของคุณ
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <span className="text-3xl">📭</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                ยังไม่มีคำขอลา
              </p>
              <div className="space-y-2 w-full">
                <Button
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 dark:from-blue-700 dark:to-purple-700 dark:hover:from-blue-800 dark:hover:to-purple-800 shadow-md"
                  onClick={() => navigate("/leave/request")}
                >
                  📝 ขอลางาน
                </Button>
                <Button
                  variant="outline"
                  className="w-full hover:bg-gray-50 dark:hover:bg-gray-800"
                  onClick={() => navigate("/leaves")}
                >
                  📋 ดูคำขอทั้งหมด
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats Card */}
        <Card className="border-purple-100 dark:border-purple-900 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 border-b dark:border-gray-700">
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <span className="text-xl">📊</span>
              สถิติ
            </CardTitle>
            <CardDescription className="dark:text-gray-400">
              สรุปการลาของคุณ
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950/50 rounded-lg border border-yellow-100 dark:border-yellow-900">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-200 dark:bg-yellow-900/50 rounded-full flex items-center justify-center">
                    <span className="text-lg">⏳</span>
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    รออนุมัติ
                  </span>
                </div>
                <span className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  0
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/50 rounded-lg border border-green-100 dark:border-green-900">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-200 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                    <span className="text-lg">✅</span>
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    อนุมัติแล้ว
                  </span>
                </div>
                <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                  0
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-950/50 rounded-lg border border-red-100 dark:border-red-900">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-200 dark:bg-red-900/50 rounded-full flex items-center justify-center">
                    <span className="text-lg">❌</span>
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    ถูกปฏิเสธ
                  </span>
                </div>
                <span className="text-2xl font-bold text-red-600 dark:text-red-400">
                  0
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Info */}
      <Card className="mt-6 border-indigo-100 dark:border-indigo-900 shadow-md hover:shadow-lg transition-shadow">
        <CardHeader className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950 dark:to-blue-950 border-b dark:border-gray-700">
          <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <span className="text-xl">👤</span>
            ข้อมูลบัญชีของคุณ
          </CardTitle>
          <CardDescription className="dark:text-gray-400">
            ข้อมูลส่วนตัวและการทำงาน
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50 rounded-lg border border-blue-100 dark:border-blue-900">
              <div className="w-10 h-10 bg-blue-200 dark:bg-blue-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-lg">📝</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  ชื่อ-นามสกุล
                </p>
                <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                  {user.firstName} {user.lastName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/50 dark:to-pink-950/50 rounded-lg border border-purple-100 dark:border-purple-900">
              <div className="w-10 h-10 bg-purple-200 dark:bg-purple-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-lg">📧</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  อีเมล
                </p>
                <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                  {user.email}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/50 dark:to-emerald-950/50 rounded-lg border border-green-100 dark:border-green-900">
              <div className="w-10 h-10 bg-green-200 dark:bg-green-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-lg">🎫</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  รหัสพนักงาน
                </p>
                <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                  {user.employeeId}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/50 dark:to-orange-950/50 rounded-lg border border-amber-100 dark:border-amber-900">
              <div className="w-10 h-10 bg-amber-200 dark:bg-amber-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-lg">💼</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  ตำแหน่ง
                </p>
                <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                  {user.position || "ไม่ระบุ"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-950/50 dark:to-cyan-950/50 rounded-lg border border-teal-100 dark:border-teal-900">
              <div className="w-10 h-10 bg-teal-200 dark:bg-teal-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-lg">✅</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  สถานะ
                </p>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.status === "active"
                      ? "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300"
                  }`}
                >
                  {user.status === "active" ? "ใช้งานอยู่" : user.status}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/50 dark:to-rose-950/50 rounded-lg border border-pink-100 dark:border-pink-900">
              <div className="w-10 h-10 bg-pink-200 dark:bg-pink-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-lg">🎭</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  สิทธิ์การใช้งาน
                </p>
                <p className="font-medium text-sm text-gray-900 dark:text-gray-100 capitalize truncate">
                  {user.role}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </MainLayout>
  );
}

export default MainLeave;
